import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";

const cls = "w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0F2C59]";

export function ImageField({ value, onChange, testid = "image" }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const up = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <div className="flex gap-2">
        <input data-testid={`field-${testid}`} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL, or upload →" />
        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" ref={ref} className="hidden" onChange={up} />
        <button type="button" data-testid={`upload-${testid}`} onClick={() => ref.current.click()} disabled={busy}
          className="whitespace-nowrap border border-[#0F2C59] text-[#0F2C59] px-4 text-sm font-semibold hover:bg-[#0F2C59] hover:text-white disabled:opacity-50">
          {busy ? "Uploading…" : "Upload"}
        </button>
      </div>
      {value && <img src={value} alt="preview" className="mt-2 h-28 object-contain border border-slate-200 bg-slate-50 p-1" />}
    </div>
  );
}

export function Field({ field, value, onChange }) {
  const v = value ?? "";
  switch (field.type) {
    case "textarea":
      return <textarea data-testid={`field-${field.key}`} rows={3} className={cls} value={v} onChange={(e) => onChange(e.target.value)} />;
    case "html":
      return <textarea data-testid={`field-${field.key}`} rows={10} className={cls + " font-mono text-xs"} value={v} onChange={(e) => onChange(e.target.value)} />;
    case "number":
      return <input data-testid={`field-${field.key}`} type="number" className={cls} value={v} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
    case "bool":
      return (
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input data-testid={`field-${field.key}`} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#0F2C59]" />
          <span className="text-sm text-slate-600">{value ? "Yes" : "No"}</span>
        </label>
      );
    case "select":
      return (
        <select data-testid={`field-${field.key}`} className={cls + " bg-white"} value={v} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "tags":
    case "list": {
      const arr = Array.isArray(value) ? value : [];
      const text = arr.join(field.type === "tags" ? ", " : "\n");
      return (
        <textarea data-testid={`field-${field.key}`} rows={field.type === "tags" ? 2 : 4} className={cls}
          placeholder={field.type === "tags" ? "Comma separated" : "One per line"}
          value={text}
          onChange={(e) => {
            const parts = field.type === "tags"
              ? e.target.value.split(",").map((x) => x.trim()).filter(Boolean)
              : e.target.value.split("\n").map((x) => x.trim()).filter(Boolean);
            onChange(parts);
          }} />
      );
    }
    case "faq": {
      const arr = Array.isArray(value) ? value : [];
      const text = arr.map((f) => `${f.q} :: ${f.a}`).join("\n");
      return (
        <textarea data-testid={`field-${field.key}`} rows={4} className={cls} placeholder="Question :: Answer (one per line)"
          value={text}
          onChange={(e) => {
            const parsed = e.target.value.split("\n").filter(Boolean).map((line) => {
              const [q, ...a] = line.split("::");
              return { q: (q || "").trim(), a: a.join("::").trim() };
            });
            onChange(parsed);
          }} />
      );
    }
    case "image":
      return <ImageField value={value} onChange={onChange} testid={field.key} />;
    default:
      return <input data-testid={`field-${field.key}`} className={cls} value={v} onChange={(e) => onChange(e.target.value)} />;
  }
}
